import type {
  AppConfigDTO,
  DeploymentStatusDTO,
  FolderDTO,
  ImageDTO,
  ImageListQuery,
  ImageListResponse,
  SessionDTO,
  StatsDTO,
} from "@shared/types";

export class ApiError extends Error {
  status: number;
  code?: string;
  fields?: Record<string, string>;
  constructor(status: number, message: string, code?: string, fields?: Record<string, string>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json" };
  let body: BodyInit | undefined;

  if (opts.body instanceof FormData) {
    body = opts.body;
  } else if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  if (method !== "GET" && method !== "HEAD") headers["x-csrf-token"] = csrfToken();

  const res = await fetch(`/api${path}`, { method, headers, body, credentials: "include", signal: opts.signal });
  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; code?: string; fields?: Record<string, string> };
    throw new ApiError(res.status, err.error ?? `Request failed (${res.status})`, err.code, err.fields);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function toQuery(q: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export interface UploadResult {
  images: ImageDTO[];
  errors: Array<{ filename: string; error: string; code?: string }>;
  replacedCount: number;
  warnings: string[];
  pagesFilesUsed: number;
}

export interface UploadFields {
  file: File;
  name?: string;
  folderId?: string | null;
  filenameMode?: "as-is" | "unique-id" | "unique-timestamp";
  duplicateStrategy?: "replace" | "copy" | "cancel";
}

/** Upload one file with progress via XHR. */
export function uploadImage(
  fields: UploadFields,
  handlers: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
): Promise<UploadResult> {
  return new Promise<UploadResult>((resolve, reject) => {
    const form = new FormData();
    form.set("file", fields.file, fields.file.name);
    if (fields.name) form.set("name", fields.name);
    form.set("folderId", fields.folderId ?? "");
    form.set("filenameMode", fields.filenameMode ?? "as-is");
    form.set("duplicateStrategy", fields.duplicateStrategy ?? "copy");

    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/images/upload");
    xhr.withCredentials = true;
    xhr.setRequestHeader("x-csrf-token", csrfToken());
    xhr.setRequestHeader("accept", "application/json");

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && handlers.onProgress) handlers.onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      const data = safeJson(xhr.responseText) as (UploadResult & { error?: string; code?: string }) | null;
      if (xhr.status >= 200 && xhr.status < 300 && data) {
        resolve(data);
      } else if (data?.error) {
        reject(new ApiError(xhr.status, data.error, (data as any).code));
      } else {
        reject(new ApiError(xhr.status || 0, "Upload failed. Retry?"));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, "Network error during upload. Retry?"));
    xhr.onabort = () => reject(new ApiError(0, "Upload cancelled."));
    handlers.signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(form);
  });
}

export const api = {
  // auth
  session: () => request<SessionDTO>("/auth/session"),
  login: (username: string, password: string, remember: boolean) =>
    request<SessionDTO>("/auth/login", { method: "POST", body: { username, password, remember } }),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<SessionDTO>("/auth/change-password", { method: "POST", body: { currentPassword, newPassword } }),

  // folders
  listFolders: () => request<{ folders: FolderDTO[]; uncategorizedCount: number }>("/folders"),
  createFolder: (name: string, parentId: string | null) =>
    request<{ folder: FolderDTO }>("/folders", { method: "POST", body: { name, parentId } }),
  renameFolder: (id: string, name: string) =>
    request<{ folder: FolderDTO }>(`/folders/${id}`, { method: "PATCH", body: { name } }),
  deleteFolder: (id: string) => request<{ ok: true }>(`/folders/${id}`, { method: "DELETE" }),

  // images
  listImages: (q: ImageListQuery) => {
    const query = toQuery({
      folderId: q.folderId === null ? "uncategorized" : q.folderId,
      recursive: q.recursive ? 1 : undefined,
      q: q.q,
      type: q.type,
      sizeBucket: q.sizeBucket,
      sort: q.sort,
      page: q.page,
      pageSize: q.pageSize,
    });
    return request<ImageListResponse>(`/images${query}`);
  },
  getImage: (id: string) => request<{ image: ImageDTO }>(`/images/${id}`),
  renameImage: (id: string, name: string) =>
    request<{ image: ImageDTO; urlChanged: boolean }>(`/images/${id}`, { method: "PATCH", body: { name } }),
  moveImage: (id: string, folderId: string | null) =>
    request<{ image: ImageDTO; urlChanged: boolean }>(`/images/${id}/move`, { method: "POST", body: { folderId } }),
  deleteImage: (id: string) => request<{ ok: true }>(`/images/${id}`, { method: "DELETE" }),
  uploadImage,

  // search / stats / deployments
  config: () => request<AppConfigDTO>("/config"),
  search: (q: string) => request<ImageListResponse>(`/search${toQuery({ q })}`),
  stats: () => request<StatsDTO>("/stats"),
  deploymentStatus: () => request<DeploymentStatusDTO>("/deployments/status"),
  runDeployment: () => request<DeploymentStatusDTO>("/deployments/run", { method: "POST" }),
};
